import { canonicalCellOrder, parseCellId, } from '@cards/engine';
import { cardRef, onDeath, } from '../authoring/index.js';
export const SLIME_RESIDUE_ID = 'slime_residue';
export const SLIME_RESIDUE_STACKS_KEY = 'stacks';
/**
 * Runtime CardQuery views intentionally expose defId rather than a registry.
 * Keep the package-owned Slime set finite and reviewable in one place.
 */
export const SLIME_DEFINITION_IDS = Object.freeze([
    'acidic_slime',
    'dividing_slime',
    'leaking_clot',
    'colony_predator',
    'slippery_slime',
    'budding_belly',
    'jelly_bastion',
    'overgulper',
    'colony_devourer',
    'blind_amoeba',
    'mnemoslime',
    'sample_collector',
    'royal_jelly',
    'slime_droplet',
    'small_slime',
    'mindless_slime',
]);
const SLIME_DEFINITION_ID_SET = new Set(SLIME_DEFINITION_IDS);
export function isSlimeDefinitionId(defId) {
    return SLIME_DEFINITION_ID_SET.has(defId);
}
export function isSlimeUnit(unit) {
    return isSlimeDefinitionId(unit.defId);
}
export function slimeDependencies(...ids) {
    return [cardRef(SLIME_RESIDUE_ID), ...ids.map(cardRef)];
}
/**
 * Raw Slime queries must declare the complete finite set they may inspect.
 * Extra refs cover mechanics such as Trail or a support effect; Set keeps
 * self-refs and repeated extras harmless and deterministic.
 */
export function slimeSetDependencies(...ids) {
    return [...new Set([...SLIME_DEFINITION_IDS, ...ids])].map(cardRef);
}
function residueEffect(ctx, cellId) {
    return ctx.q.effectsOn({ kind: 'cell', cellId })
        .find((effect) => effect.defId === SLIME_RESIDUE_ID);
}
export function residueStacks(ctx, cellId) {
    const value = residueEffect(ctx, cellId)?.vars[SLIME_RESIDUE_STACKS_KEY];
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : 0;
}
export function addSlimeResidue(ctx, cellId, amount) {
    if (!Number.isSafeInteger(amount) || amount <= 0) {
        throw new Error(`addSlimeResidue: amount должен быть положительным safe integer (получено ${String(amount)})`);
    }
    const effect = residueEffect(ctx, cellId);
    if (effect === undefined) {
        const effectId = ctx.ops.addEffect({
            defId: SLIME_RESIDUE_ID,
            scope: { kind: 'cell', cellId },
            persistent: true,
            vars: { [SLIME_RESIDUE_STACKS_KEY]: 0 },
        });
        ctx.ops.modifyEffectCounter(effectId, SLIME_RESIDUE_STACKS_KEY, amount);
    }
    else {
        ctx.ops.modifyEffectCounter(effect.id, SLIME_RESIDUE_STACKS_KEY, amount);
    }
}
export function spendSlimeResidue(ctx, cellId, amount) {
    if (!Number.isSafeInteger(amount) || amount <= 0) {
        throw new Error(`spendSlimeResidue: amount должен быть положительным safe integer (получено ${String(amount)})`);
    }
    const effect = residueEffect(ctx, cellId);
    const current = residueStacks(ctx, cellId);
    if (effect === undefined || current < amount)
        return false;
    ctx.ops.modifyEffectCounter(effect.id, SLIME_RESIDUE_STACKS_KEY, -amount);
    if (current === amount)
        ctx.ops.removeEffect(effect.id);
    return true;
}
export function totalResidueForPlayer(ctx, controllerId) {
    const side = ctx.q.player(controllerId).side;
    return canonicalCellOrder.reduce((total, cellId) => {
        const cell = ctx.q.cell(cellId);
        return cell?.side === side ? total + residueStacks(ctx, cellId) : total;
    }, 0);
}
export function trail(amount = 1) {
    return onDeath((ctx, event) => {
        addSlimeResidue(ctx, event.cellId, amount);
    });
}
export function tableOf(cellId) {
    return parseCellId(cellId).tableId;
}
export function slimeUnitsInLocation(ctx, controllerId, tableId, excluded = new Set()) {
    return ctx.q.unitsOf(controllerId).filter((unit) => !excluded.has(unit.id)
        && unit.cellId !== undefined
        && tableOf(unit.cellId) === tableId
        && isSlimeUnit(unit));
}
function stepSelection(ctx, stepId) {
    return ctx.plan?.selections?.find((step) => step.stepId === stepId)?.selection ?? [];
}
export function plannedUnitIds(ctx, stepId) {
    return stepSelection(ctx, stepId).flatMap((candidate) => candidate.kind === 'unit' ? [candidate.instanceId] : []);
}
export function plannedUnitId(ctx, stepId) {
    return plannedUnitIds(ctx, stepId)[0];
}
export function plannedCellId(ctx, stepId) {
    const candidate = stepSelection(ctx, stepId).find((item) => item.kind === 'cell');
    return candidate?.kind === 'cell' ? candidate.cellId : undefined;
}
export function plannedTableId(ctx, stepId) {
    const candidate = stepSelection(ctx, stepId).find((item) => item.kind === 'table');
    return candidate?.kind === 'table' ? candidate.tableId : undefined;
}
export function selectedUnitId(previous, stepId) {
    const candidate = previous.find((step) => step.stepId === stepId)?.selection
        .find((item) => item.kind === 'unit');
    return candidate?.kind === 'unit' ? candidate.instanceId : undefined;
}
export function selectedPublicUnit(context, previous, stepId) {
    const id = selectedUnitId(previous, stepId);
    return id === undefined ? undefined : context.units.find((unit) => unit.instanceId === id);
}
//# sourceMappingURL=slime-helpers.js.map